import React, { useState, useEffect } from 'react';

interface EmailPreferences {
  emailNotificationsEnabled: boolean;
  notificationEmail: string;
  emailVerified: boolean;
  emailStatus: 'active' | 'bounced' | 'complained' | 'invalid';
  lastNotificationSent?: string;
}

interface EmailSettingsProps {
  userId: string;
}

const EmailSettings: React.FC<EmailSettingsProps> = ({ userId }) => {
  const [preferences, setPreferences] = useState<EmailPreferences>({
    emailNotificationsEnabled: true,
    notificationEmail: '',
    emailVerified: false,
    emailStatus: 'active',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

  useEffect(() => {
    fetchPreferences();
  }, [userId]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINT}/profile/email-preferences?userId=${userId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch email preferences');
      }
      
      const data = await response.json();
      setPreferences(data.emailPreferences);
    } catch (err) {
      setError('Failed to load email preferences');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const updatedPrefs = {
        ...preferences,
        emailNotificationsEnabled: !preferences.emailNotificationsEnabled,
      };
      
      const response = await fetch(`${API_ENDPOINT}/profile/email-preferences?userId=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedPrefs),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update preferences');
      }
      
      const data = await response.json();
      setPreferences(data.emailPreferences);
      setSuccess('Preferences updated successfully');
    } catch (err) {
      setError('Failed to update preferences');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPreferences({
      ...preferences,
      notificationEmail: e.target.value,
    });
  };

  const handleEmailSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(preferences.notificationEmail)) {
        setError('Please enter a valid email address');
        setSaving(false);
        return;
      }
      
      const response = await fetch(`${API_ENDPOINT}/profile/email-preferences?userId=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update email');
      }
      
      const data = await response.json();
      setPreferences(data.emailPreferences);
      setSuccess('Email address updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to update email address');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = () => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      bounced: 'bg-red-100 text-red-800',
      complained: 'bg-red-100 text-red-800',
      invalid: 'bg-yellow-100 text-yellow-800',
    };
    
    const statusLabels = {
      active: 'Active',
      bounced: 'Bounced',
      complained: 'Complained',
      invalid: 'Invalid',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[preferences.emailStatus]}`}>
        {statusLabels[preferences.emailStatus]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800">📧 Email Notifications</h3>
        {getStatusBadge()}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      <div className="space-y-4">
        {/* Toggle Switch */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
          <div>
            <p className="font-medium text-gray-800">Enable Email Notifications</p>
            <p className="text-sm text-gray-600">Receive period predictions via email</p>
          </div>
          <button
            onClick={handleToggle}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              preferences.emailNotificationsEnabled ? 'bg-purple-600' : 'bg-gray-300'
            } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                preferences.emailNotificationsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Email Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              value={preferences.notificationEmail}
              onChange={handleEmailChange}
              placeholder="your.email@example.com"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={saving}
            />
            <button
              onClick={handleEmailSave}
              disabled={saving || !preferences.notificationEmail}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            You'll receive notifications 7 days before your predicted period
          </p>
        </div>

        {/* Status Information */}
        {preferences.emailStatus !== 'active' && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              {preferences.emailStatus === 'bounced' && 'Your email address bounced. Please update it.'}
              {preferences.emailStatus === 'complained' && 'Email notifications have been disabled due to a complaint.'}
              {preferences.emailStatus === 'invalid' && 'Your email address appears to be invalid. Please update it.'}
            </p>
          </div>
        )}

        {preferences.lastNotificationSent && (
          <div className="text-xs text-gray-500">
            Last notification sent: {new Date(preferences.lastNotificationSent).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailSettings;
